/**
 * Demo Request Service
 * Handles sending demo request emails via the Medica backend API
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const SEND_EMAIL_ENDPOINT = `${API_BASE_URL}/user/send-email`;
const DEMO_REQUEST_EMAIL = import.meta.env.VITE_DEMO_REQUEST_EMAIL;

/**
 * Generate professional HTML email template for demo request
 */
const generateDemoEmailHTML = (formData) => {
  const { name, email, organisationName, mobileNumber, message } = formData;

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
          background-color: #f3f4f6;
          padding: 20px;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .header {
          background: linear-gradient(135deg, #7096F3 0%, #4181F3 40%, #C08EDB 100%);
          padding: 20px 20px;
          text-align: center;
          color: white;
        }
        .header h1 {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 8px;
        }
        .header p {
          font-size: 14px;
          opacity: 0.9;
        }
        .content {
          padding: 20px 20px;
        }
        .greeting {
          font-size: 16px;
          color: #1f2937;
          margin-bottom: 24px;
          line-height: 1.5;
        }
        .info-section {
          background-color: #f9fafb;
          border-left: 4px solid #c8102e;
          padding: 16px;
          margin-bottom: 24px;
          border-radius: 6px;
        }
        .info-section h3 {
          font-size: 13px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }
        .info-row {
          margin-bottom: 12px;
          font-size: 14px;
          color: #1f2937;
          line-height: 1.6;
        }
        .info-row strong {
          color: #374151;
          font-weight: 600;
          display: block;
          font-size: 13px;
          color: #6b7280;
          margin-bottom: 4px;
        }
        .message-box {
          background-color: #eff6ff;
          border: 1px solid #bfdbfe;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 24px;
        }
        .message-box h3 {
          font-size: 13px;
          font-weight: 600;
          color: #1e40af;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .message-box p {
          font-size: 14px;
          color: #1f2937;
          line-height: 1.6;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .cta-section {
          background-color: #f0fdf4;
          border: 1px solid #bbf7d0;
          padding: 20px;
          border-radius: 6px;
          text-align: center;
          margin-bottom: 24px;
        }
        .cta-button {
          display: inline-block;
          background: linear-gradient(135deg, #7096F3 0%, #4181F3 40%, #C08EDB 100%);
          color: white;
          padding: 12px 28px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
        }
        .footer {
          background-color: #f9fafb;
          padding: 24px 20px;
          text-align: center;
          border-top: 1px solid #e5e7eb;
        }
        .footer p {
          font-size: 12px;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 8px;
        }
        .footer a {
          color: #4181F3;
          text-decoration: none;
        }
        .footer-social {
          margin-top: 12px;
          font-size: 12px;
          color: #9ca3af;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <!-- Header -->
        <div class="header">
          <h2>New Demo Request Received</h2>
        </div>

        <!-- Content -->
        <div class="content">
          <div class="greeting">
            <p>Hello,</p>
            <p>A new demo request has been submitted through the Medica website. Here are the details:</p>
          </div>

          <!-- Contact Information -->
          <div class="info-section">
            <h3>Contact Information</h3>
            <div class="info-row">
              <strong>Name</strong>
              ${name || 'Not provided'}
            </div>
            <div class="info-row">
              <strong>Email</strong>
              ${email || 'Not provided'}
            </div>
            <div class="info-row">
              <strong>Mobile Number</strong>
              ${mobileNumber || 'Not provided'}
            </div>
            <div class="info-row">
              <strong>Organisation Name</strong>
              ${organisationName || 'Not provided'}
            </div>
          </div>

          <!-- Demo Request Message -->
          <div class="message-box">
            <h3>Demo Request Details</h3>
            <p>${message || 'No message provided'}</p>
          </div>

          <!-- CTA Section -->
          <div class="cta-section">
            <p style="font-size: 14px; color: #1f2937; margin-bottom: 12px;">
              <strong>Ready to respond?</strong><br>
              Contact this prospect as soon as possible to schedule their demo.
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="footer">
          <p>This is an automated message from the Medica Demo Request System.</p>
          <p><a href="https://www.medica.lk">Visit Medica</a> | <a href="https://www.medica.lk/contact-us">Contact Support</a></p>
          <div class="footer-social">
            <p>© 2026 Medica. All rights reserved.</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Send demo request email
 * @param {Object} formData - Form data containing name, email, organisationName, mobileNumber, message
 * @returns {Promise<Object>} API response
 */
export const sendDemoRequestEmail = async (formData) => {
  try {
    const emailHTML = generateDemoEmailHTML(formData);

    const payload = {
      to: DEMO_REQUEST_EMAIL,
      subject: `New Demo Request from ${formData.name} - Medica`,
      html: emailHTML,
      attachments: []
    };

    const response = await fetch(SEND_EMAIL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Email sending failed with status ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      message: 'Demo request email sent successfully',
      data
    };
  } catch (error) {
    console.error('Error sending demo request email:', error);
    return {
      success: false,
      message: error.message || 'Failed to send demo request email',
      error
    };
  }
};

export default {
  sendDemoRequestEmail,
  generateDemoEmailHTML
};
